import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';

type LoggerFunction = (message: string, meta?: object) => void;

/**
 * Extract a human-readable error message from an Axios error.
 */
function extractErrorMessage(e: unknown, method: string, path: string): string {
  if (axios.isAxiosError(e)) {
    const axiosErr = e as AxiosError<{ message?: string | string[] }>;
    const serverMsg = axiosErr.response?.data?.message;
    const status = axiosErr.response?.status ?? 'unknown';
    const detail = Array.isArray(serverMsg) ? serverMsg.join(', ') : serverMsg;
    return detail
      ? `${method} ${path} failed (${status}): ${detail}`
      : `${method} ${path} failed (${status})`;
  }
  return (e as Error)?.message ?? `${method} ${path} failed`;
}

export class ApiClient {
  private axiosInstance: AxiosInstance;
  private logger?: LoggerFunction;
  host: string;

  constructor(host: string, logger?: LoggerFunction) {
    this.host = host;
    this.axiosInstance = axios.create({
      baseURL: host
    });
    this.logger = logger;
  }

  /**
   * Log messages if logger is enabled
   * @param message - Log message
   * @param meta - Additional meta information
   */
  private log(message: string, meta?: object) {
    if (this.logger) {
      this.logger(message, meta);
    }
  }

  /**
   * Send a GET request
   * @param path - The API path
   * @param params - Query parameters
   * @param config - Optional Axios request config
   * @returns Response data
   */
  async get(
    path: string,
    params?: object,
    config?: AxiosRequestConfig
  ): Promise<any> {
    this.log(`GET Request: ${path}`, { params });
    try {
      const response = await this.axiosInstance.get(path, {
        ...config,
        params
      });
      this.log(`GET Response: ${path}`, {
        status: response.status,
        data: response.data
      });
      return response.data;
    } catch (e) {
      const msg = extractErrorMessage(e, 'GET', path);
      this.log(`GET Error: ${path}`, { error: msg });
      console.error(msg);
      throw new Error(msg);
    }
  }

  /**
   * Send a POST request
   * @param path - The API path
   * @param data - The request body (JSON or FormData)
   * @param config - Optional Axios request config
   * @returns Response data
   */
  async post(
    path: string,
    data?: object | FormData,
    config?: AxiosRequestConfig
  ): Promise<any> {
    this.log(`POST Request: ${path}`, { data });
    try {
      const response = await this.axiosInstance.post(
        path,
        data,
        this.applyHeadersForData(data, config)
      );
      this.log(`POST Response: ${path}`, {
        status: response.status,
        data: response.data
      });
      return response.data;
    } catch (e) {
      const msg = extractErrorMessage(e, 'POST', path);
      this.log(`POST Error: ${path}`, { error: msg });
      console.error(msg);
      throw new Error(msg);
    }
  }

  /**
   * Send a PUT request
   * @param path - The API path
   * @param data - The request body (JSON or FormData)
   * @param config - Optional Axios request config
   * @returns Response data
   */
  async put(
    path: string,
    data?: object | FormData,
    config?: AxiosRequestConfig
  ): Promise<any> {
    this.log(`PUT Request: ${path}`, { data });
    try {
      const response = await this.axiosInstance.put(
        path,
        data,
        this.applyHeadersForData(data, config)
      );
      this.log(`PUT Response: ${path}`, {
        status: response.status,
        data: response.data
      });
      return response.data;
    } catch (e) {
      const msg = extractErrorMessage(e, 'PUT', path);
      this.log(`PUT Error: ${path}`, { error: msg });
      console.error(msg);
      throw new Error(msg);
    }
  }

  /**
   * Send a PATCH request
   * @param path - The API path
   * @param data - The request body (JSON or FormData)
   * @param config - Optional Axios request config
   * @returns Response data
   */
  async patch(
    path: string,
    data?: object | FormData,
    config?: AxiosRequestConfig
  ): Promise<any> {
    this.log(`PATCH Request: ${path}`, { data });
    try {
      const response = await this.axiosInstance.patch(
        path,
        data,
        this.applyHeadersForData(data, config)
      );
      this.log(`PATCH Response: ${path}`, {
        status: response.status,
        data: response.data
      });
      return response.data;
    } catch (e) {
      const msg = extractErrorMessage(e, 'PATCH', path);
      this.log(`PATCH Error: ${path}`, { error: msg });
      console.error(msg);
      throw new Error(msg);
    }
  }

  /**
   * Send a DELETE request
   * @param path - The API path
   * @param data - Optional body (JSON or FormData)
   * @param config - Optional Axios request config
   * @returns Response data
   */
  async delete(
    path: string,
    data?: object | FormData,
    config?: AxiosRequestConfig
  ): Promise<any> {
    this.log(`DELETE Request: ${path}`, { data });
    try {
      const response = await this.axiosInstance.delete(path, {
        ...this.applyHeadersForData(data, config),
        data
      });
      this.log(`DELETE Response: ${path}`, {
        status: response.status,
        data: response.data
      });
      return response.data;
    } catch (e) {
      const msg = extractErrorMessage(e, 'DELETE', path);
      this.log(`DELETE Error: ${path}`, { error: msg });
      console.error(msg);
      throw new Error(msg);
    }
  }

  /**
   * Determines if data is FormData and applies appropriate headers
   * @param data - The request body (JSON or FormData)
   * @param config - Existing Axios request config
   * @returns Updated Axios request config with headers
   */
  private applyHeadersForData(
    data: any,
    config?: AxiosRequestConfig
  ): AxiosRequestConfig {
    if (data instanceof FormData) {
      return {
        ...config,
        headers: {
          ...(config?.headers || {}),
          'Content-Type': 'multipart/form-data'
        }
      };
    }
    return config || {};
  }
}
